import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { BancoldexService } from 'src/app/comun/services/bancoldex.service';
import { HOME_CONTENT_MOCK } from '../data/home-content.mock';
import type {
  HomeValorBannersActivosResponse,
  HomeValorContent,
} from '../models/home-valor-content.model';
import { mapValorBannersToContent } from '../utils/home-valor-banner.mapper';
import { prepareValorIntroHtml, prepareValorTitleHtml } from '../utils/home-valor-html.util';

/** Debe coincidir con `NeoBannerDTO.DESCRIPCION_HOME` */
export const VALOR_BANNER_TIPO = 'DescripcionHome';

@Injectable({
  providedIn: 'root',
})
export class HomeValorService {
  private readonly fallbackContent: HomeValorContent = buildValorFallbackFromMock();

  constructor(private readonly bancoldexService: BancoldexService) {}

  loadContent(): Observable<HomeValorContent> {
    return this.bancoldexService.getArchivosBannerActivos(VALOR_BANNER_TIPO).pipe(
      map((response: HomeValorBannersActivosResponse) => {
        const rows = normalizeValorBannerRows(response);
        if (!rows.length) {
          return this.fallbackContent;
        }
        return mapValorBannersToContent(rows, this.fallbackContent);
      }),
      catchError(() => of(this.fallbackContent))
    );
  }
}

function buildValorFallbackFromMock(): HomeValorContent {
  const mock = HOME_CONTENT_MOCK.valor;
  return {
    eyebrow: mock.eyebrow,
    titleHtml: prepareValorTitleHtml(mock.titleHtml),
    introHtml: prepareValorIntroHtml(mock.introHtml),
    beneficios: mock.beneficios.map((benefit) => ({ ...benefit })),
  };
}

/** El API devuelve lista plana para `Bienvenido` y `DescripcionHome`; otros tipos vienen agrupados. */
function normalizeValorBannerRows(
  response: HomeValorBannersActivosResponse
): HomeValorBannersActivosResponse['banners'] {
  if (!response?.exitoso || !response.banners?.length) {
    return [];
  }

  const first = response.banners[0] as unknown;
  if (Array.isArray(first)) {
    return (response.banners as unknown as HomeValorBannersActivosResponse['banners'][]).flat();
  }

  return response.banners;
}
